/**
 * 下载中心：模块级单例（XHR 进度下载 + 弹层 UI）
 */
import { computed, reactive, ref } from 'vue';
import { ElMessage } from 'element-plus';
import { downloadFileByBlob } from '@/utils/api';
import i18n from '@/plugins/i18n';

const t = i18n.global.t;

/** sessionStorage 键名：批量下载异步任务 ID 列表，用于轮询查询 */
const BATCH_TASK_IDS_KEY = 'BATCH_DOWNLOAD_TASK_IDS';

const PROGRESS_CAP = 100;
const MAX_CONCURRENT = 3;
/** XMLHttpRequest 表示响应成功的 HTTP 状态码 */
const HTTP_OK = 200;
const FAKE_PROGRESS_INTERVAL = 500;
/** 模拟进度上限，留余量给真实完成 */
const FAKE_PROGRESS_MAX = 90;
/** 每轮递增时取剩余量的比例，越小越平滑 */
const FAKE_PROGRESS_RATE = 0.08;
const ASYNC_POLL_INTERVAL = 3000;
/** 异步任务完成后，仅在此时间窗口内自动触发下载（ms，2 分钟） */
const ASYNC_FRESH_THRESHOLD = 120_000;
const MAX_EMPTY_POLLS = 3;
const ASYNC_STATUS = { PENDING: 0, PROCESSING: 1, SUCCESS: 2, FAILED: 3 };
const ASYNC_STATUS_MAP = {
  [ASYNC_STATUS.PENDING]: 'pending',
  [ASYNC_STATUS.PROCESSING]: 'downloading',
  [ASYNC_STATUS.SUCCESS]: 'completed',
  [ASYNC_STATUS.FAILED]: 'failed'
};
/** 网络恢复后等待确认的延迟时间（ms），防止 online 事件误触 */
const ONLINE_CONFIRM_DELAY = 1500;
let asyncPollTimer = null;
let emptyPollCount = 0;
const asyncDownloadedIds = new Set();
const asyncPolling = ref(false);
let asyncFetchFn = null;
let asyncRetryFn = null;
let asyncCancelFn = null;

/** 网络离线标志（响应式，供 pumpQueue 等逻辑判断） */
const isOffline = ref(typeof window !== 'undefined' ? !navigator.onLine : false);
/** 恢复网络后延迟确认计时器，防止 online 事件误触 */
let onlineConfirmTimer = null;

const panelVisible = ref(false);
const expanded = ref(true);

function setExpanded(val) {
  expanded.value = val;
}

function showDownloadPanel() {
  panelVisible.value = true;
}

function hideDownloadPanelOnly() {
  panelVisible.value = false;
}

let idSeq = 0;

function genDownloadTaskId() {
  return `dn-${Date.now()}-${++idSeq}`;
}

const tasks = ref([]);

const isDownloading = computed(() => tasks.value.some((t) => t.status === 'pending' || t.status === 'downloading'));

const aggregateProgress = computed(() => {
  const list = tasks.value.filter((t) => t.status === 'downloading' || t.status === 'pending');
  if (list.length === 0) return 0;
  let sum = 0;
  list.forEach((t) => {
    sum += t.status === 'pending' ? 0 : Math.min(PROGRESS_CAP, t.progress || 0);
  });
  return Math.round(sum / list.length);
});

let runningCount = 0;

/** 批量下载时累计完成/成功/失败计数，全部结束后触发 onComplete 回调 */
function notifyBatchTerminal(task) {
  const ctx = task.batchCtx;
  if (!ctx) return;
  ctx.done = (ctx.done || 0) + 1;
  if (task.status === 'completed') ctx.success = (ctx.success || 0) + 1;
  else if (task.status === 'failed') ctx.fail = (ctx.fail || 0) + 1;
  else if (task.status === 'cancelled') ctx.cancelled = (ctx.cancelled || 0) + 1;
  const total = ctx.total;
  if (typeof total === 'number' && ctx.done >= total) {
    ctx.onComplete?.(ctx);
  }
}

/** 将文件名中不允许的字符（\/:*?"<>|）替换为下划线，避免写入文件系统时报错 */
function sanitizeFileName(name) {
  let s = String(name || 'file');
  for (const ch of '\\/:*?"<>|') {
    s = s.split(ch).join('_');
  }
  return s;
}

/** 接口无真实进度时，前端以递减增量模拟进度增长，避免进度条静止 */
function startFakeProgress(task) {
  if (task._fakeTimer) return;
  task._fakeTimer = setInterval(() => {
    if (task.status !== 'downloading' || task._hasRealProgress) {
      stopFakeProgress(task);
      return;
    }
    const remaining = FAKE_PROGRESS_MAX - task.progress;
    const increment = Math.max(1, Math.floor(remaining * FAKE_PROGRESS_RATE));
    task.progress = Math.min(FAKE_PROGRESS_MAX, task.progress + increment);
  }, FAKE_PROGRESS_INTERVAL);
}

function stopFakeProgress(task) {
  if (task._fakeTimer) {
    clearInterval(task._fakeTimer);
    task._fakeTimer = null;
  }
}

/**
 * 通过 <a download> 触发浏览器原生下载（XHR 场景专用，无 user activation）
 */
function persistDownloadedBlob(blob, fileName) {
  downloadFileByBlob(blob, sanitizeFileName(fileName));
}

/**
 * 使用浏览器原生下载（<a download>），不弹出"另存为"对话框。
 */
async function triggerFileDownload(url, fileName) {
  const safeName = sanitizeFileName(fileName);
  try {
    const resp = await fetch(url);
    const blob = await resp.blob();
    downloadFileByBlob(blob, safeName);
  } catch {
    /* 静默：文件可能已被删除或链接过期 */
  }
}

/** 通过 XHR 下载文件 Blob 并持久化到本地，维护任务状态与并发计数 */
function runDownloadWithXhr(task) {
  const xhr = new XMLHttpRequest();
  let settled = false;

  function settleTerminal() {
    if (settled) return;
    settled = true;
    stopFakeProgress(task);
    task._abort = null;
    runningCount--;
    pumpQueue();
    notifyBatchTerminal(task);
  }

  task._abort = () => {
    try {
      xhr.abort();
    } catch {
      /* ignore */
    }
  };

  task.status = 'downloading';
  task.progress = 0;
  task._hasRealProgress = false;
  startFakeProgress(task);

  xhr.open('GET', task.fileUrl, true);
  xhr.responseType = 'blob';

  xhr.onprogress = (ev) => {
    if (task._cancelRequested || task.status !== 'downloading') return;
    if (ev.lengthComputable && ev.total > 0) {
      if (!task._hasRealProgress) {
        task._hasRealProgress = true;
        stopFakeProgress(task);
      }
      task.progress = Math.min(PROGRESS_CAP, Math.round((ev.loaded / ev.total) * PROGRESS_CAP));
    }
  };

  xhr.onerror = () => {
    if (settled) return;
    if (task._offlineAbort || isOffline.value) {
      // 断网引发的错误：标记为暂停，等待恢复网络后重试，不通知批量回调
      task._offlineAbort = true;
      task.status = 'paused';
      task.progress = 0;
      task.errorMessage = '';
      stopFakeProgress(task);
      task._abort = null;
      settled = true;
      runningCount--;
      return;
    }
    if (!task._cancelRequested) {
      task.status = 'failed';
      task.errorMessage = t('cloudDocument.download_center.network_error');
      task.progress = 0;
    }
    settleTerminal();
  };

  xhr.onabort = () => {
    if (settled) return;
    if (task._offlineAbort) {
      // 断网触发的 abort：标记为暂停，不通知批量回调
      task.status = 'paused';
      task.progress = 0;
      task.errorMessage = '';
      stopFakeProgress(task);
      task._abort = null;
      settled = true;
      runningCount--;
      return;
    }
    task.status = 'cancelled';
    task.progress = 0;
    task.errorMessage = '';
    settleTerminal();
  };

  xhr.onload = () => {
    if (settled) return;
    if (xhr.status === 0) {
      task.status = task._cancelRequested ? 'cancelled' : 'failed';
      if (!task._cancelRequested) {
        task.errorMessage = 'Network error';
      }
      task.progress = 0;
      settleTerminal();
      return;
    }
    if (task._cancelRequested) {
      task.status = 'cancelled';
      task.progress = 0;
      settleTerminal();
      return;
    }
    if (xhr.status !== HTTP_OK) {
      task.status = 'failed';
      task.errorMessage = `HTTP ${xhr.status}`;
      task.progress = 0;
      settleTerminal();
      return;
    }

    const blob = xhr.response;
    if (!(blob instanceof Blob)) {
      task.status = 'failed';
      task.errorMessage = 'Invalid response';
      task.progress = 0;
      settleTerminal();
      return;
    }

    persistDownloadedBlob(blob, task.fileName);

    if (task._cancelRequested) {
      task.status = 'cancelled';
      task.progress = 0;
    } else {
      task.status = 'completed';
      task.progress = PROGRESS_CAP;
      task.errorMessage = '';
    }
    settleTerminal();
  };

  try {
    xhr.send();
  } catch (e) {
    task.status = 'failed';
    task.errorMessage = e?.message || String(e);
    task.progress = 0;
    settleTerminal();
  }
}

/** 创建响应式下载任务对象 */
function createDownloadTask(payload) {
  return reactive({
    taskId: payload.taskId,
    id: genDownloadTaskId(),
    taskKind: 'file',
    fileName: payload.fileName || 'file',
    fileUrl: payload.fileUrl,
    status: 'pending',
    progress: 0,
    errorMessage: '',
    documentId: payload.documentId,
    batchCtx: payload.batchCtx || null,
    _abort: null,
    _cancelRequested: false,
    _offlineAbort: false
  });
}

/** 从任务队列中取出 pending 任务，直到达到最大并发数 MAX_CONCURRENT */
function pumpQueue() {
  if (isOffline.value) return;
  while (runningCount < MAX_CONCURRENT) {
    const next = tasks.value.find((t) => t.taskKind === 'file' && t.status === 'pending');
    if (!next) break;
    runningCount++;
    runDownloadWithXhr(next);
  }
}

/**
 * @param {object} payload
 * @param {string} payload.fileUrl
 * @param {string} [payload.fileName]
 * @param {number|string} [payload.documentId]
 * @param {object} [payload.batchCtx]
 */
function enqueueDownload(payload) {
  const task = createDownloadTask(payload);
  tasks.value.push(task);
  showDownloadPanel();
  pumpQueue();
  return task.id;
}

/**
 * @param {Array<{ fileUrl: string, fileName?: string, documentId?: number|string }>} items
 * @param {{ onComplete?: function }} [options]
 */
function downloadFiles(items, options = {}) {
  const { onComplete } = options;
  const list = Array.isArray(items) ? items : [];
  if (list.length === 0) return [];

  const batchCtx = {
    total: list.length,
    done: 0,
    success: 0,
    fail: 0,
    cancelled: 0,
    onComplete
  };

  const ids = [];
  list.forEach((item) => {
    const id = enqueueDownload({
      taskId: item.taskId,
      fileUrl: item.fileUrl,
      fileName: item.fileName,
      documentId: item.documentId,
      batchCtx
    });
    ids.push(id);
  });
  return ids;
}

/** 取消 async 任务：成功则移除并重启轮询，失败则保留任务 */
async function cancelAsyncTask(task) {
  stopFakeProgress(task);
  if (!asyncCancelFn || !task.asyncTaskId) return;
  try {
    await asyncCancelFn({ taskId: task.asyncTaskId }, { hideMessage: true });
    ElMessage.success(t('cloudDocument.download_center.cancel_success'));
    const idx = tasks.value.indexOf(task);
    if (idx !== -1) tasks.value.splice(idx, 1);
    removeAsyncTaskIdFromStorage(task.asyncTaskId);
    if (asyncFetchFn && !asyncPolling.value) {
      startAsyncPoll(asyncFetchFn, { retryFn: asyncRetryFn, cancelFn: asyncCancelFn });
    }
  } catch {
    ElMessage.error(t('cloudDocument.download_center.cancel_fail'));
  }
}

/** 取消单个任务：async 任务调用服务端取消接口并重新轮询，file 任务本地取消 */
async function cancelTask(taskId) {
  const task = tasks.value.find((t) => t.id === taskId);
  if (!task) return;

  if (task.taskKind === 'async') {
    await cancelAsyncTask(task);
    return;
  }

  if (task.taskKind !== 'file') return;

  if (task.status === 'pending' || task.status === 'paused') {
    task._cancelRequested = true;
    task._offlineAbort = false;
    task.status = 'cancelled';
    task.progress = 0;
    notifyBatchTerminal(task);
    return;
  }

  if (task.status === 'downloading') {
    task._cancelRequested = true;
    task._abort?.();
  }
}

/** 取消所有进行中的任务，但不从列表移除（保留记录供 UI 展示） */
function cancelAll() {
  tasks.value.forEach((task) => {
    if (task.status === 'downloading' || task.status === 'pending') {
      stopFakeProgress(task);
      task._cancelRequested = true;
      task._abort?.();
      if (task.status === 'pending') {
        task.status = 'cancelled';
        notifyBatchTerminal(task);
      }
    } else if (task.status === 'paused') {
      // 暂停中的任务：直接标为取消并通知批量回调
      task._offlineAbort = false;
      task.status = 'cancelled';
      notifyBatchTerminal(task);
    }
  });
}

/** 获取 sessionStorage 中的所有批量下载任务 ID */
function getBatchTaskIds() {
  try {
    return JSON.parse(sessionStorage.getItem(BATCH_TASK_IDS_KEY) || '[]');
  } catch {
    return [];
  }
}

/** 从 sessionStorage 的任务 ID 列表中移除指定 ID */
function removeAsyncTaskIdFromStorage(asyncTaskId) {
  try {
    const ids = getBatchTaskIds();
    const updated = ids.filter((id) => id !== asyncTaskId);
    sessionStorage.setItem(BATCH_TASK_IDS_KEY, JSON.stringify(updated));
  } catch {
    /* ignore */
  }
}

/** 向 sessionStorage 的任务 ID 列表追加新 ID（去重） */
function addAsyncTaskIdToStorage(asyncTaskId) {
  try {
    const ids = getBatchTaskIds();
    if (!ids.includes(asyncTaskId)) {
      ids.push(asyncTaskId);
    }
    sessionStorage.setItem(BATCH_TASK_IDS_KEY, JSON.stringify(ids));
  } catch {
    /* ignore */
  }
}

/** 关闭面板时调用：停止异步轮询、取消所有任务并清空列表 */
function clearAll() {
  stopAsyncPoll();
  cancelAll();
  tasks.value = [];
  panelVisible.value = false;
  expanded.value = true;
  try {
    sessionStorage.removeItem(BATCH_TASK_IDS_KEY);
  } catch {
    /* ignore */
  }
}

/** 包含异步轮询状态，用于关闭面板时判断是否需要二次确认 */
function hasRunningTask() {
  return (
    asyncPolling.value ||
    tasks.value.some((t) => t.status === 'downloading' || t.status === 'pending' || t.status === 'paused')
  );
}

/** 重试失败的任务：file 类型直接重新入队，async 类型调用服务端重试接口并重启轮询 */
async function retryTask(taskId) {
  const task = tasks.value.find((t) => t.id === taskId);
  if (!task || task.status !== 'failed') return;

  if (task.taskKind === 'async') {
    if (!asyncRetryFn) return;
    stopFakeProgress(task);
    task.status = 'downloading';
    task.progress = 0;
    task.errorMessage = '';
    task._hasRealProgress = false;
    try {
      const res = await asyncRetryFn({ taskId: task.asyncTaskId }, { hideMessage: true });
      const newTaskId = res?.data?.taskId;
      if (newTaskId && newTaskId !== task.asyncTaskId) {
        // 后端重试返回新 taskId：更新 sessionStorage 并同步任务对象
        // （findOrCreateAsyncTask 用 id = `async-${taskId}` 匹配，必须保持一致）
        removeAsyncTaskIdFromStorage(task.asyncTaskId);
        addAsyncTaskIdToStorage(newTaskId);
        task.asyncTaskId = newTaskId;
        task.taskId = newTaskId;
        task.id = `async-${newTaskId}`;
      }
      // 不从列表移除：避免下一轮 findOrCreate 时再次 push 把该条挤到列表底部
      if (asyncFetchFn) {
        startAsyncPoll(asyncFetchFn, { retryFn: asyncRetryFn, cancelFn: asyncCancelFn });
      }
    } catch {
      task.status = 'failed';
      task.errorMessage = t('cloudDocument.download_center.retry_fail');
    }
    return;
  }

  if (task.taskKind !== 'file') return;
  task.batchCtx = null;
  task.status = 'pending';
  task.progress = 0;
  task.errorMessage = '';
  task._cancelRequested = false;
  showDownloadPanel();
  pumpQueue();
}

/** 重新下载已完成的任务：async 任务用 resultUrl 触发保存，file 任务重置入队 */
function redownloadTask(taskId) {
  const task = tasks.value.find((t) => t.id === taskId);
  if (!task) return;

  if (task.taskKind === 'async' && task.resultUrl) {
    triggerFileDownload(task.resultUrl, task.fileName);
    return;
  }

  if (task.taskKind !== 'file' || (task.status !== 'completed' && task.status !== 'cancelled')) return;
  task.batchCtx = null;
  task.status = 'pending';
  task.progress = 0;
  task.errorMessage = '';
  task._cancelRequested = false;
  showDownloadPanel();
  pumpQueue();
}

function toggleExpand() {
  setExpanded(!expanded.value);
}

/**
 * 根据 taskId 查找或创建一条异步任务在列表中的展示条目。
 * 顺序：轮询里按 res.data 顺序 forEach，新建任务 push 顺序即接口顺序；已存在则原地更新，重试勿 splice 再 push（否则会沉底）。
 */
function findOrCreateAsyncTask(item) {
  const id = `async-${item.taskId}`;
  let task = tasks.value.find((t) => t.id === id);
  if (!task) {
    task = reactive({
      id,
      taskId: item.taskId,
      taskKind: 'async',
      asyncTaskId: item.taskId,
      fileName: item.resultFileName || item.fileName || String(item.taskId),
      resultUrl: item.resultUrl || '',
      status: ASYNC_STATUS_MAP[+item.status] || 'pending',
      progress: item.progressPercent || 0,
      errorMessage: '',
      _fakeTimer: null,
      _hasRealProgress: false
    });
  } else {
    task.status = ASYNC_STATUS_MAP[+item.status] || task.status;
    task.resultUrl = item.resultUrl || task.resultUrl;
    task.progress = item.progressPercent > task.progress ? item.progressPercent : task.progress;
  }
  return task;
}

/**
 * 轮询查询无响应或抛错时：停止前端模拟进度条，并将进行中的异步任务标为失败
 */
function failInFlightAsyncTasksFromPollError() {
  tasks.value.forEach((task) => {
    if (task.taskKind !== 'async') return;
    if (task.status !== 'pending' && task.status !== 'downloading') return;
    stopFakeProgress(task);
    task.status = 'failed';
    task.errorMessage = t('cloudDocument.batch_download.query_failed');
    task.progress = 0;
    task._hasRealProgress = false;
  });
}

/**
 * 启动异步下载任务轮询。面板立即可见，轮询期间显示 loading。
 * @param {function} fetchFn - 无参函数，调用后返回响应体（含 data 数组）
 * @param {{ retryFn?: function, cancelFn?: function }} [options]
 */
function startAsyncPoll(fetchFn, options = {}) {
  stopAsyncPoll();
  asyncFetchFn = fetchFn;
  if (options.retryFn) asyncRetryFn = options.retryFn;
  if (options.cancelFn) asyncCancelFn = options.cancelFn;
  showDownloadPanel();
  asyncPolling.value = true;
  emptyPollCount = 0;

  const poll = async () => {
    try {
      const res = await fetchFn();

      if (!res) {
        if (isOffline.value) {
          stopAsyncPoll();
          return;
        }
        ElMessage.error(t('cloudDocument.batch_download.query_failed'));
        failInFlightAsyncTasksFromPollError();
        stopAsyncPoll();
        return;
      }

      const taskItems = res.data || [];

      if (taskItems.length === 0) {
        emptyPollCount++;
        if (emptyPollCount >= MAX_EMPTY_POLLS) {
          stopAsyncPoll();
        }
        return;
      }

      emptyPollCount = 0;
      let hasProcessing = false;

      const newTasks = taskItems.map((item) => {
        const status = +item.status;
        const task = findOrCreateAsyncTask(item);
        switch (status) {
          case ASYNC_STATUS.PENDING:
          case ASYNC_STATUS.PROCESSING: {
            hasProcessing = true;
            const apiProgress = +(item.progressPercent || 0);
            if (apiProgress > 0) {
              task._hasRealProgress = true;
              stopFakeProgress(task);
              task.progress = Math.min(FAKE_PROGRESS_MAX, apiProgress);
            } else if (!task._hasRealProgress) {
              startFakeProgress(task);
            }
            break;
          }
          case ASYNC_STATUS.SUCCESS: {
            stopFakeProgress(task);
            const isFresh = item.updateTime && Date.now() - new Date(item.updateTime).getTime() < ASYNC_FRESH_THRESHOLD;
            if (isFresh && item.resultUrl && !asyncDownloadedIds.has(item.taskId)) {
              asyncDownloadedIds.add(item.taskId);
              triggerFileDownload(item.resultUrl, task.fileName);
            }
            break;
          }
          case ASYNC_STATUS.FAILED: {
            stopFakeProgress(task);
            task.errorMessage = item.failReason || item.errorMessage || '';
            break;
          }
          default:
            break;
        }
        return task;
      });
      tasks.value = [...newTasks];

      if (!hasProcessing) {
        stopAsyncPoll();
      }
    } catch {
      if (isOffline.value) {
        // 断网导致的轮询失败：停止轮询，保留任务状态（handleOffline 已设为 paused）
        stopAsyncPoll();
        return;
      }
      ElMessage.error(t('cloudDocument.batch_download.query_failed'));
      failInFlightAsyncTasksFromPollError();
      stopAsyncPoll();
    }
  };

  poll();
  asyncPollTimer = setInterval(poll, ASYNC_POLL_INTERVAL);
}

/** 停止轮询定时器并重置状态，由 clearAll / 轮询完成 / 接口异常 触发 */
function stopAsyncPoll() {
  if (asyncPollTimer) {
    clearInterval(asyncPollTimer);
    asyncPollTimer = null;
  }
  emptyPollCount = 0;
  asyncPolling.value = false;
}

/**
 * 断网处理：停止轮询，中止所有活跃 XHR 下载，将任务置为暂停状态。
 * async 任务保留 taskId 以便恢复网络后重启轮询同步后端真实状态。
 */
function handleOffline() {
  if (isOffline.value) return;
  isOffline.value = true;
  if (onlineConfirmTimer) {
    clearTimeout(onlineConfirmTimer);
    onlineConfirmTimer = null;
  }

  // 停止异步轮询，但不调用 failInFlightAsyncTasksFromPollError（保留状态）
  stopAsyncPoll();

  tasks.value.forEach((task) => {
    if (task.taskKind === 'async') {
      if (task.status === 'pending' || task.status === 'downloading') {
        stopFakeProgress(task);
        task._offlineAbort = true;
        task.status = 'paused';
      }
    } else if (task.taskKind === 'file') {
      if (task.status === 'downloading') {
        task._offlineAbort = true;
        task._abort?.(); // onabort 回调会将状态设为 paused
      } else if (task.status === 'pending') {
        task._offlineAbort = true;
        task.status = 'paused';
      }
    }
  });
}

/**
 * 恢复网络处理（延迟 1.5s 确认）：重启异步轮询，重新入队被暂停的 file 任务。
 */
function handleOnline() {
  if (onlineConfirmTimer) clearTimeout(onlineConfirmTimer);
  onlineConfirmTimer = setTimeout(() => {
    onlineConfirmTimer = null;
    if (!isOffline.value) return;
    isOffline.value = false;

    // 恢复 async 任务：重置状态并重启轮询（轮询第一轮会同步后端真实状态）
    let hasAsyncPaused = false;
    tasks.value.forEach((task) => {
      if (task.taskKind === 'async' && task._offlineAbort) {
        task._offlineAbort = false;
        task.status = 'downloading';
        task.progress = 0;
        hasAsyncPaused = true;
      }
    });
    if (hasAsyncPaused && asyncFetchFn) {
      startAsyncPoll(asyncFetchFn, { retryFn: asyncRetryFn, cancelFn: asyncCancelFn });
    }

    // 恢复 file 任务：重置为 pending 并重新入队
    tasks.value.forEach((task) => {
      if (task.taskKind !== 'file' || !task._offlineAbort) return;
      task._offlineAbort = false;
      task._cancelRequested = false;
      task.status = 'pending';
      task.progress = 0;
      task.errorMessage = '';
    });
    pumpQueue();
  }, ONLINE_CONFIRM_DELAY);
}

// 模块初始化：注册网络监听
if (typeof window !== 'undefined') {
  window.addEventListener('offline', handleOffline);
  window.addEventListener('online', handleOnline);
}

const downloadCenterApi = {
  panelVisible,
  expanded,
  asyncPolling,
  isOffline,
  setExpanded,
  toggleExpand,
  showPanel: showDownloadPanel,
  hidePanelOnly: hideDownloadPanelOnly,
  tasks,
  isDownloading,
  aggregateProgress,
  enqueueDownload,
  downloadFiles,
  cancelTask,
  cancelAll,
  clearAll,
  retryTask,
  redownloadTask,
  hasRunningTask,
  startAsyncPoll,
  stopAsyncPoll,
  // 导出 sessionStorage 工具函数供外部复用
  getBatchTaskIds,
  addAsyncTaskIdToStorage,
  removeAsyncTaskIdFromStorage
};

/**
 * @returns {typeof downloadCenterApi}
 */
export function useDownloadCenter() {
  return downloadCenterApi;
}
